/**
 * Lightweight route guard for the admin portal.
 *
 * @remarks
 * We intentionally avoid introducing a heavyweight AuthProvider layer here. Instead we rely on the shared
 * `useAuthState()` hook and (when needed) check custom claims on the current user.
 *
 * Logging note: keep logs low-noise and avoid dumping full claims or PII to the console.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { useAuthState } from "../../HelloCashflowDomain/Authentication/hooks/useAuthState";
import { useAdminLogout } from "@/hooks/useAdminLogout";
import { isAdminE2eBypassEnabled } from "@/utils/featureFlags";
import AdminAccessDeniedPanel from "./AdminAccessDeniedPanel";
import RouteGuardLoadingState from "./RouteGuardLoadingState";
import { useAdminClaimCheck } from "./hooks/useAdminClaimCheck";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "SimpleProtectedRoute",
);

interface SimpleProtectedRouteProps {
    children: React.ReactNode;
    requiredClaims?: string[];
    fallbackPath?: string;
}

/**
 * Simple Protected Route that uses the proven working Firebase pattern
 * No complex AuthProvider needed - works directly with global Firebase state
 */
const SimpleProtectedRoute: React.FC<SimpleProtectedRouteProps> = ({
    children,
    requiredClaims = [],
    fallbackPath = "/login",
}) => {
    const { user, loading } = useAuthState();
    const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
    const location = useLocation();
    const { logout, isLoggingOut } = useAdminLogout();
    const { hasAdminClaim, isCheckingClaims, requiresAdminClaim } =
        useAdminClaimCheck({
            adminE2eBypassEnabled,
            requiredClaims,
            user,
        });

    if (adminE2eBypassEnabled) {
        return <>{children}</>;
    }

    // Now handle rendering based on state
    // CRITICAL: Block rendering while auth loading (prevents race conditions)
    if (loading) {
        logger.debug("Auth still loading; blocking route render", {
            operation: "auth_loading",
        });
        return <RouteGuardLoadingState message='Loading…' />;
    }

    // Redirect to login if not authenticated
    if (!user) {
        logger.debug("No authenticated user; redirecting to login", {
            operation: "auth_redirect_login",
            fallbackPath,
        });
        return (
            <Navigate to={fallbackPath} state={{ from: location }} replace />
        );
    }

    logger.debug("User authenticated; checking route permissions", {
        operation: "auth_user_present",
        userId: user.uid,
        requiredClaims,
    });

    // Show loading while checking claims
    if (requiresAdminClaim && isCheckingClaims) {
        return <RouteGuardLoadingState message='Checking permissions…' />;
    }

    // Check admin claims if required
    if (requiresAdminClaim && !hasAdminClaim) {
        return (
            <AdminAccessDeniedPanel
                isLoggingOut={isLoggingOut}
                onLogout={() => void logout()}
                user={user}
            />
        );
    }

    // User is authenticated and authorized
    return <>{children}</>;
};

export default SimpleProtectedRoute;
